import description from "./trade-params-description";
import {
    SavedSettingsState,
    ParamGroupType,
    SavedSattingsItemAbstract,
    SavedCPSettingsState,
    SavedSettingsItem,
    SavedSettingsItemAffiliation,
} from "store/reducers/SettingsSlice";

export type ParamType = "Float" | "Integer" | "String" | "Boolean";

type Param = {
    key: string;
    type: ParamType;
};

interface CastedDesctiption {
    [key: string]: ParamGroupType;
}

export type TradeParamsRequestName = "ticker_apply";
export type TradeParamsRequestBody = TickerApplyRequestBody;
export interface TradeParamsRequest {
    group_name: string;
    ticker_name: string;
    CP: string[][];
    SP: string[][];
    MDP: string[][];
}

type SavedSettings = {
    [key in ParamGroupType]: string[][];
};

type _SavedSettings = {
    name: ParamGroupType;
    data: string[][];
};

export interface TickerApplyRequestBody {
    group_name: string;
    ticker_name: string;
    settings: SavedSettingsState;
    saved_settings: SavedSettings;
    saved_CP_settings: SavedCPSettingsState;
}

type ValidationError = {
    exchange_name: string;
    field: string;
    invalid_value: string;
};

const validations = {
    Float: (x: any) => {
        // this code is needed if the customer whats to make validation back

        // if (+x === 0) return true; // zero is float either
        // console.log(formattedValue.split(".").length);
        // if(formattedValue.split(".").length !== 2) return false // only one point can have appeared

        const formattedValue = x.replaceAll(",", "."); // formate a string
        if (isNaN(+formattedValue)) return false; // only numbers and "." (",")

        const parsed = parseFloat(formattedValue);
        if (isNaN(parsed)) {
            return false;
        }
        return true;
    },
    Integer: (x: any) => {
        return /^[0-9]+$/.test(x);
    },
    String: (x: any) => {
        return typeof x === "string";
    },
    Boolean: (x: any) => {
        const allowed_values = [0, 1];
        return allowed_values.some((v) => x.toString() === v?.toString());
    },
};

export default class TradeParamsHandler {
    static _param_types = [];
    static _trade_params_desc = [];
    static _casted_description: CastedDesctiption | undefined = undefined;

    static stateHasChanges({
        saved_settings,
        affiliation,
    }: {
        saved_settings: SavedSettingsItem[];
        affiliation: SavedSettingsItemAffiliation;
    }) {
        const affiliation_keys = Object.keys(affiliation);
        return saved_settings.some(
            (x) =>
                affiliation_keys.every(
                    (key) => x?.affiliation[key] === affiliation[key]
                ) && x.has_change
        );
    }

    static uncastCP(data: SavedSattingsItemAbstract[]) {
        const keys: string[] = [];
        const values: string[] = [];
        for (let index = 0; index < data.length; index++) {
            const item = data[index];
            keys.push(item.key);
            values.push(item.value);
        }
        return {
            keys,
            values,
        };
    }

    static castCP(keys: string[], values: string[], has_change?: string) {
        const data: SavedSattingsItemAbstract[] = [];
        for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            const value = values[index];
            const type = this.findParamType(key);
            const item: SavedSattingsItemAbstract = {
                key,
                value,
                type,
            };
            if (typeof has_change === "string" && key === has_change) {
                item.has_change = true;
            }
            data.push(item);
        }
        return data;
    }

    static buildRequestBody(
        request: TradeParamsRequestName,
        body: TradeParamsRequestBody
    ): TradeParamsRequest[] {
        const data: TickerApplyRequestBody = JSON.parse(JSON.stringify(body));
        switch (request) {
            case "ticker_apply": {
                const output = [];
                const {
                    group_name,
                    ticker_name,
                    settings,
                    saved_settings,
                    saved_CP_settings,
                } = data;
                const affiliation = {
                    ticker_name,
                    group_name,
                };
                let cp_settings: string[][] = JSON.parse(
                    JSON.stringify(saved_settings.CP)
                );
                const changed: SavedSettingsItem[] = [];

                const exchanges = [];
                const MDP_list = saved_settings.MDP[1].every((x) =>
                    Array.isArray(x)
                )
                    ? saved_settings.MDP[1]
                    : [saved_settings.MDP[1]];
                for (const mdp of MDP_list) {
                    if (Array.isArray(mdp) && mdp.length) {
                        const exchange_name = mdp[2];
                        exchanges.push(exchange_name);
                    }
                }
                if (typeof saved_CP_settings === "object") {
                    for (const key of Object.keys(saved_CP_settings)) {
                        const items = saved_CP_settings[key];
                        if (!Array.isArray(items)) {
                            continue;
                        }
                        for (const item of items) {
                            if (
                                Object.keys(affiliation).every(
                                    (x) =>
                                        affiliation[x] === item.affiliation[x]
                                ) &&
                                item.has_change
                            ) {
                                changed.push(item);
                            }
                        }
                    }
                }
                for (const item of changed) {
                    const with_desc = item.key.includes("(");
                    const cp_key = with_desc
                        ? item.key.split("(")[0].trim()
                        : item.key;
                    const cp_desc = with_desc
                        ? item.key
                              .split("(")[1]
                              .slice(0, item.key.split("(")[1].length - 1)
                        : null;
                    const start_index = cp_desc
                        ? cp_settings[0].findIndex((x) => x === cp_desc)
                        : 0;
                    const index =
                        cp_settings[0]
                            .slice(start_index)
                            .findIndex((x) => x === cp_key) + start_index;
                    if (index !== -1) {
                        cp_settings[1][index] = item.value;
                    }
                }
                let CP: _SavedSettings = {
                    name: "CP",
                    data: cp_settings,
                };
                let MDP: _SavedSettings = {
                    name: "MDP",
                    data: saved_settings.MDP,
                };
                let SP: _SavedSettings = {
                    name: "SP",
                    data: saved_settings.SP,
                };
                const default_request_body = {
                    group_name,
                    ticker_name,
                    CP: CP.data,
                    SP: SP.data,
                    MDP: MDP.data,
                };
                if (default_request_body.MDP[0]?.includes("is_active")) {
                    default_request_body.MDP = default_request_body.MDP.map(
                        (x) => x.slice(0, 5)
                    );
                }
                for (const exchange_name of exchanges) {
                    const items = settings.filter((item) => {
                        const affiliation = {
                            group_name,
                            ticker_name,
                            exchange_name,
                        };
                        return (
                            item.affiliation &&
                            Object.keys(affiliation).every(
                                (key) =>
                                    affiliation[key] === item.affiliation[key]
                            )
                        );
                    });
                    const has_change = items.some((x) => x.has_change);
                    if (has_change) {
                        let _data = JSON.parse(JSON.stringify({ CP, SP, MDP }));
                        const keys: ParamGroupType[] = [CP, SP, MDP].map(
                            (x) => x.name
                        );
                        for (let x = 0; x < items.length; x++) {
                            const item = items[x];
                            const affiliation = this.getParamAffilitation(
                                item.key
                            );
                            if (!keys.includes(affiliation)) {
                                continue;
                            }
                            let data: string[][] = [..._data[affiliation].data];
                            if (item.has_change) {
                                let copy = data.slice();
                                const array_index = copy.findIndex((x) =>
                                    x.includes(exchange_name)
                                );
                                const element_index = copy[0].findIndex(
                                    (x) => x === item.key
                                );
                                if (
                                    [array_index, element_index].some(
                                        (index) => index === -1
                                    )
                                ) {
                                    continue;
                                }
                                copy[array_index][element_index] = item.value;
                                data = copy;
                            }
                            _data[affiliation].data = data;
                        }

                        const __SP = _data.SP.data.filter(
                            (x, i) => i === 0 || x[0] === exchange_name
                        );
                        const __MDP_values = _data.MDP.data[1].filter(
                            (x, i) => Array.isArray(x) && x[2] === exchange_name
                        );
                        let __MDP = [_data.MDP.data[0], ...__MDP_values];
                        __MDP = __MDP.map((x) => x.slice());
                        const __CP = _data.CP.data;
                        // Needs check. ScheduleID is empty when new ticker added to group
                        if (__CP[1][0] === "") {
                            __CP[1][0] = "1";
                        }
                        if (__MDP.length === 1) {
                            __MDP.push(
                                _data.MDP.data[1].slice(0, __MDP[0].length)
                            );
                        }

                        for (let x = 1; x < __MDP.length; x++) {
                            const params = __MDP[0];
                            let list = __MDP[x];
                            if (params.length > list.length) {
                                for (let y = 0; y < params.length; y++) {
                                    const observed_param = params[y];
                                    if (observed_param === "Flag") {
                                        __MDP[x].push("0");
                                        continue;
                                    }
                                    if (typeof list[y] === "undefined") {
                                        __MDP[x].push("");
                                    }
                                }
                            }
                        }

                        const request_body = {
                            ...default_request_body,
                            CP: __CP,
                            SP: __SP,
                            MDP: __MDP,
                        };
                        output.push(request_body);
                        continue;
                    }
                    const default_mdp = default_request_body.MDP[1].every((x) =>
                        Array.isArray(x)
                    )
                        ? default_request_body.MDP[1].filter(
                              (x) => Array.isArray(x) && x[2] === exchange_name
                          )
                        : [default_request_body.MDP[1]];

                    output.push({
                        ...default_request_body,
                        MDP: [default_request_body.MDP[0], ...default_mdp],
                        SP: [
                            default_request_body.SP[0],
                            ...default_request_body.SP.slice(1).filter(
                                (x) =>
                                    Array.isArray(x) && x[0] === exchange_name
                            ),
                        ],
                    });
                }
                console.log("output", output);
                // No changes for SP pararms were found
                if (!output.length) {
                    return [default_request_body];
                }
                return output;
            }
            default:
                return undefined;
        }
    }

    /**
     * Cast data from { [param_group]: [[param_name]...] } to { [param_name]: [param_group] }
     */
    static castDescription(desc: typeof description) {
        let output = {};
        const param_groups = Object.keys(desc);
        for (const group_key of param_groups) {
            const params = desc[group_key];
            for (let x = 0; x < params.length; x++) {
                const param = params[x];
                output = {
                    ...output,
                    [param]: group_key,
                };
            }
        }

        return output;
    }

    static set param_types(p: Param[]) {
        const params = this.param_types;
        let _p = p;
        if (params.length) {
            _p = params.concat(...p);
        }
        this._param_types = _p;
    }

    static get param_types() {
        return this._param_types;
    }

    /**
     * @example
     * input: ID.depend
     * ouput: CP
     */
    static getParamAffilitation(param_name: string): ParamGroupType {
        const casted_description = this._casted_description;
        if (typeof casted_description !== "undefined") {
            return casted_description[param_name];
        }

        const casted = this.castDescription(description);
        this._casted_description = casted;
        return casted[param_name];
    }

    /**
     * @example
     * input: ID.depend
     * output: Integer
     */
    static findParamType(key: string) {
        const params = this.param_types;

        if (!params.length) {
            return null;
        }

        const found = params.find((x) => x.key === key);
        if (found) {
            return found.type;
        }

        return null;
    }

    static validate(
        data: SavedSettingsState,
        strict?: boolean
    ): ValidationError[] {
        const errors = [];
        const keys = data
            .map((x) => x.affiliation && x.affiliation.exchange_name)
            .filter((x) => typeof x === "string");

        for (const exchange_name of keys) {
            const items = data.filter(
                (x) => x.affiliation.exchange_name === exchange_name
            );
            for (const item of items) {
                const valid = this._validate({ ...item, strict });
                if (!valid) {
                    errors.push({
                        exchange_name,
                        field: item.key,
                        invalid_value: item.value,
                    });
                }
            }
        }
        console.log("errors", errors);
        return errors;
    }

    static validateMDP(mdp: string[]) {
        const checks = [
            {
                name: "has_Source.Type",
                message: "Отсутствует Source.Type для: ",
                fn: (data: string[]) => {
                    return typeof data[3] === "string" && data[3].length;
                },
            },
        ];

        for (const check of checks) {
            const test = check.fn(mdp);
            if (!test) {
                return {
                    error: check.message + mdp[2],
                };
            }
        }

        return true;
    }

    /**
     * true - valid
     * false - invalidr
     */
    static _validate({
        value,
        key,
        strict,
    }: {
        value: any;
        key: string;
        strict?: boolean;
    }) {
        if (!strict) {
            if (value === "") {
                return true;
            }
        }

        const type = this.findParamType(key);

        //console.log(`key: ${key} | type: ${type}`);
        if (!type) {
            return false;
        }

        return validations[type](value);
    }

    static castSPData(SP: string[][]): SavedSattingsItemAbstract[] {
        const excluded_params = ["ID.source"];
        let output = [];
        if (!Array.isArray(SP)) {
            return;
        }
        for (let x = 1; x < SP.length; x++) {
            // 1st array - SP keys
            // rest - SP values
            // all ordered
            const part = SP[x];
            const exchange_name = part[0];
            const to_state = [];
            for (let y = 0; y < part.length; y++) {
                const key = SP[0][y];
                const value = part[y];
                if (excluded_params.includes(key)) {
                    continue;
                }
                to_state.push({
                    key,
                    value,
                    affiliation: {
                        exchange_name,
                    },
                });
            }
            output.push(...to_state);
        }
        return output;
    }
    /**
     * data should contain
     * Source.def.par.group.ID or
     * All other fields
     */
    static checkSP(data: string[][]) {
        if (!Array.isArray(data) || (Array.isArray(data) && data.length < 2)) {
            return {
                error: "Некорректные данные SP",
            };
        }

        const passed_checks: string[] = [];
        const min_checks_count = 1;
        const default_error_message = "Заполните необходимые параметры SP";
        const checks = [
            {
                name: "should_contain_Source.def.par.group.ID",
                error_msg: default_error_message,
                fn: (data: string[][]) => {
                    const required_param = "Source.def.par.group.ID";
                    const param_index = data[0].findIndex(
                        (x) => x === required_param
                    );
                    if (param_index === -1) {
                        return false;
                    }
                    for (let x = 1; x < data.length; x++) {
                        const param_value = data[x][param_index];
                        if (
                            !(
                                typeof param_value === "string" &&
                                param_value.length > 0
                            )
                        ) {
                            return false;
                        }
                    }
                    return true;
                },
            },
            {
                name: "all_fields_must_be_filled",
                error_msg: default_error_message,
                fn: (data: string[][]) => {
                    for (let x = 1; x < data.length; x++) {
                        const filled_values = data[x].filter(
                            (x) => x && x !== ""
                        );
                        // -1 (Source.def.par.group.ID)
                        if (filled_values.length < data[x].length - 1) {
                            return false;
                        }
                    }
                    return true;
                },
            },
        ];

        for (const check of checks) {
            const test = check.fn(data);
            if (test) {
                passed_checks.push(check.name);
            }
        }
        if (passed_checks.length >= min_checks_count) {
            return {
                completed: true,
            };
        }

        return {
            error: default_error_message,
        };
    }
}
